import { Controller } from "@hotwired/stimulus"

// Resilience fallback for the application "reading…" page.
//
// The status panel normally updates live via an Action Cable / Turbo Stream broadcast
// when the extraction job finishes. If that broadcast never reaches the browser — e.g.
// the WebSocket can't connect through a production proxy — the spinner would hang even
// though the job already completed. So while the read is in progress we also re-fetch the
// page every few seconds and swap the status region in; once it renders the completed
// state, the redirect controller inside it takes over and advances the user.
//
// This element is rendered ONLY in the in-progress branch, so swapping in the finished
// state removes it and stops the poll. A ceiling on attempts guards against a truly stuck
// job hammering the server forever.
export default class extends Controller {
  static values = {
    url: String, // page to re-fetch (the intake show page)
    region: String, // id of the status container to swap
    interval: { type: Number, default: 3000 },
    max: { type: Number, default: 100 }, // ~5 min ceiling, then give up quietly
  }

  connect() {
    this.tries = 0
    this.timer = setInterval(() => this.check(), this.intervalValue)
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
  }

  async check() {
    if ((this.tries += 1) > this.maxValue) return this.disconnect()

    let html
    try {
      const resp = await fetch(this.urlValue || window.location.href, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
      })
      if (!resp.ok) return // transient (auth blip, 5xx) — try again next tick
      html = await resp.text()
    } catch {
      return // network blip — try again next tick
    }

    const fresh = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById(this.regionValue)
    const current = document.getElementById(this.regionValue)
    if (fresh && current && fresh.innerHTML !== current.innerHTML) {
      // Assigning innerHTML lets Stimulus connect any controllers in the new markup
      // (notably `redirect`, which then navigates to the prefilled form).
      current.innerHTML = fresh.innerHTML
    }
  }
}
